import { ElMessage } from 'element-plus';
import i18n from './locales.js';
import store from './store/store.js';
export const messages = () => {
  /**
   * Enhanced error handler to work with the new backend error structure
   * New error structure includes:
   * - status_code: HTTP status code
   * - message: User-friendly error message
   * - type: Exception type name
   * - details: Original error details
   * - timestamp: ISO format timestamp
   * - field: Specific field that caused validation error (for 400 errors)
   * - request_context: Request info (in DEBUG mode)
   * - traceback: Full traceback (for 500+ errors in DEBUG mode)
   */
  const showerorr = (msg) => {
    let text = '';
    let data;
    let stat;
    let type = 'warning';
    
    // Extract error data from response
    try {
      if (msg.response !== undefined) {
        data = msg.response.data.error;
        stat = msg.response.status;
      } else if (msg.data !== undefined && msg.status !== undefined) {
        data = msg.data.error;
        stat = msg.status;
      }
    } catch {
      return;
    }
    
    // Log detailed error info to console for debugging
    console.group(`🔴 API Error [${stat}] - ${data?.type || 'Unknown'}`);
    console.log('Status:', stat);
    console.log('Message:', data?.message);
    console.log('Type:', data?.type);
    console.log('Timestamp:', data?.timestamp);
    if (data?.field) {
      console.log('Field:', data.field);
    }
    if (data?.request_context) {
      console.log('Request Context:', data.request_context);
    }
    if (data?.details) {
      console.log('Details:', data.details);
    }
    if (data?.traceback) {
      console.log('Traceback:', data.traceback);
    }
    console.groupEnd();
    
    // Handle different status codes
    if (stat == 401) {
      // Unauthorized - use the enhanced message
      text = data.message || 'Authentication required';
      type = 'warning';
    } 
    else if (stat == 403) {
      // Forbidden - use the enhanced message
      text = data.message || 'Permission denied';
      type = 'warning';
      
      // Handle specific cases
      if (data.details?.code === "token_not_valid") {
        text = "Session ended";
      } else if (data.details?.code === "not_authenticated") {
        text = data.message;
      }
    } 
    else if (stat == 400) {
      // Bad Request - validation errors
      type = 'warning';
      
      /**
       * Recursively process nested field errors for human-friendly display
       * Handles grid columns, nested objects, and arrays
       */
      const processFieldErrors = (errors, parentField = '', level = 0) => {
        let errorText = '';
        
        Object.entries(errors).forEach(([key, value]) => {
          const fieldName = store.state.ui.field_translations[key] || key;
          const fullFieldName = parentField ? `${parentField} → ${fieldName}` : fieldName;
          
          if (value.constructor == Array) {
            // Check if array contains objects (nested errors like grid rows)
            if (value.length > 0 && value[0].constructor == Object) {
              // Multiple rows with errors
              value.forEach(function (item, index) {
                const rowPrefix = i18n.t('misc.line') || 'Row';
                errorText += `<strong>${fullFieldName} (${rowPrefix} ${index + 1}):</strong><br />`;
                errorText += processFieldErrors(item, '', level + 1);
              });
            } else {
              // Simple array of error messages
              const errorMsg = value[0] || 'Error';
              if (level === 0) {
                errorText += `<strong>${fieldName}:</strong> ${errorMsg}<br />`;
              } else {
                errorText += `&nbsp;&nbsp;• ${fieldName}: ${errorMsg}<br />`;
              }
            }
          } else if (value.constructor == Object) {
            // Nested object (like grid column errors)
            errorText += `<strong>${fullFieldName}:</strong><br />`;
            errorText += processFieldErrors(value, '', level + 1);
          } else {
            // Simple string error
            if (level === 0) {
              errorText += `<strong>${fieldName}:</strong> ${value}<br />`;
            } else {
              errorText += `&nbsp;&nbsp;• ${fieldName}: ${value}<br />`;
            }
          }
        });
        
        return errorText;
      };
      
      // Use the enhanced message if available and it's a simple error
      if (data.message && data.message !== 'Bad Request') {
        text = data.message;
        
        // If there's a specific field, highlight it
        if (data.field) {
          const fieldName = store.state.ui.field_translations[data.field] || data.field;
          text = `<strong>${fieldName}:</strong> ${text}`;
        }
      } else if (typeof data === 'string' || data instanceof String) {
        text = data;
      } else if (data.details) {
        // Process detailed validation errors (including nested ones)
        text = processFieldErrors(data.details);
        
        // If no text was generated, fall back to the message
        if (!text && data.message) {
          text = data.message;
        }
      }
    }
    else if (stat == 404) {
      // Not Found
      text = data.message || 'Resource not found';
      type = 'warning';
    } 
    else if (stat >= 500) {
      // Server Error
      type = 'error';
      text = data.message || 'Internal server error';
      
      // In development, show traceback if available
      if (data.traceback) {
        console.error('Server Traceback:', data.traceback);
        text += '<br /><br /><small>Check console for detailed traceback</small>';
      }
      
      // Show exception message if available
      if (data.exception_message) {
        text += `<br /><br /><strong>Error:</strong> ${data.exception_message}`;
      }
      
      // Show timestamp for support
      if (data.timestamp) {
        text += `<br /><small>Timestamp: ${data.timestamp}</small>`;
      }
    } 
    else {
      // Other errors
      text = data?.message || msg.message || 'An error occurred';
    }
    
    // Display the error message
    ElMessage({
      message: text,
      type: type,
      grouping: true,
      center: true,
      duration: type === 'error' ? 10000 : 7000,
      showClose: true,
      dangerouslyUseHTMLString: true
    });
  };

  const notify = (type, title) => {
    ElMessage({
        message: title,
        type: type,
        grouping: true,
        duration: type == 'success' ? 1500 : 10000
    })
  };

  return {showerorr,notify}
}
